import { memo, useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { cn, Discount, Price } from '@divlab/divanui';
import { IconPlusBold, IconEmptyMessage } from '@divlab/divanui/icons';

import * as PickPairStore from '@Stores/PickPair';
import { isInViewport } from '@Utils/isInViewport';
import * as DeliveryTerms from '@Queries/DeliveryTerms';
import * as ProductSellPoints from '@Queries/ProductSellPoints';
import * as Profile from '@Queries/Profile';
import * as PageProductStore from '@Stores/PageProduct';
import * as ModalWindows from '@Stores/ModalWindows';
import * as HeaderStore from '@Stores/Header';
import useNavigation from '@Navigation/useNavigation';
import useMedias from '@Hooks/useMedias';
import { useRequest } from '@Contexts/Request';
import { useDeps } from '@Contexts/DI';
import { useFeatureFlags } from '@Contexts/FeatureFlags';
import { useUpdateComponentStorage } from '@Stores/ComponentStorage';
import getProductName from '@Utils/getProductName';
import OnSaleSoon from '@Pages/PageProduct/elements/OnSaleSoon';
import AboutReceiptButton from '@Components/AboutReceiptButton';
import ProductLike from '@Components/ProductLike';
import Fabrics from '@Components/Fabrics';
import AsyncOrderBonuses from '@Components/AsyncOrderBonuses';
import { useMeta } from '@Queries/Meta';
import useTranslation from '@Queries/useTranslation';
import Button from '@UI/Button';
import Link from '@UI/Link';
import YandexSplitBadge from '@Components/YandexSplitBadge';
import YandexSplitWidget from '@Components/YandexSplitWidget';
import LinksList from '../LinksList';
import BuyButton from '../BuyButton';
import OutOfStock from '../OutOfStock';
import CreditTabWrapper from './elements/CreditTabWrapper';
import StickyBuyButton from '../StickyBuyButton';
import Option from './elements/Option';
import styles from './Sidebar.module.css';

import type { FC, HTMLAttributes, MouseEvent, PropsWithChildren } from 'react';
import type { ColorData, CreditData } from '@Types/Product';
import type { PecomDeliveryParameters } from '@Components/PecomDelivery';
import type { PickPairData } from '@Api/Product/getSetItem';

export interface SidebarProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  goodsInfo?: PecomDeliveryParameters[];
  onClickCharacteristics?: (e: MouseEvent) => void;
  onClickReviews?: (e: MouseEvent) => void;
}

export interface ContainerProps extends PropsWithChildren {
  className?: string;
  scrollable?: boolean;
  cnLayer?: string;
}

interface Params {
  product: PickPairData;
  onApply?: (item: PickPairData) => void;
}

const Sidebar: FC<SidebarProps> = (props) => {
  const { className, onClickCharacteristics, onClickReviews, goodsInfo, ...restProps } = props;
  const page = PageProductStore.useData();
  const { isMobileM } = useMedias();
  const { t } = useTranslation();
  const { pathname } = useNavigation();
  const countOfChangedOptions = PageProductStore.getCountOfChangedParameters();
  const {
    data: { region, currency },
  } = useMeta();
  const hasReadySolutions = page.readySolutions?.colors?.length > 0;
  const { personalAreaAvailable } = useFeatureFlags();
  const { isUzb, isRus } = useRequest();
  const { totalHeight } = HeaderStore.useSizes();
  const refBuyButton = useRef<HTMLDivElement>(null);
  const refActions = useRef<HTMLDivElement>(null);
  const [buttonInView, setButtonInView] = useState(false);
  const deliveryPrice = parseInt(page.deliveryPage.price) || null;
  const profile = Profile.useProfile();
  const isClientDesigner = profile.data?.clientType === 'designer';
  const isBonusesShown = page.isAvailable && !isClientDesigner && personalAreaAvailable;
  const { analytics } = useDeps();
  const textOutOfStock = t('unavailable');
  const { products, additional } = PickPairStore.useSet();
  const actions = PickPairStore.useActions();
  const { price, credit } = PickPairStore.useProductInfo();
  const currentPrice = price || page.product.price;
  const hasExpired = currentPrice.expired > 0;
  const hasDiscount = currentPrice.discount > 0;
  const ids = PickPairStore.useSelectedSet();
  const [disabled, setDisabled] = useState(false);
  const hasWarnings = PickPairStore.useHasWarnings();
  const isComingSoon = page.product.status === 'ComingSoon';

  const deliveryTerms = DeliveryTerms.useDeliveryTerms({
    productId: page.product.id,
  });

  const showrooms = ProductSellPoints.useProductSellPoints({
    productId: page.product.id,
  });

  const deliveryDate = useMemo(() => {
    if (!deliveryTerms.data) return null;

    return deliveryTerms.data[0];
  }, [deliveryTerms.data]);

  const showDeliveryDate = deliveryDate && !countOfChangedOptions;

  const findMainCredit = useCallback((creditData: CreditData[]) => {
    const availableCredit = creditData.find((creditInfo) => creditInfo.isMain);

    if (availableCredit) {
      return availableCredit;
    }

    return creditData.find((creditInfo) => creditInfo.creditAvailable);
  }, []);

  const mainCredit = useMemo(() => {
    if (!page.credit && !credit) return null;

    if (credit) {
      return findMainCredit(credit);
    }

    return findMainCredit(page.credit);
  }, [page.credit, credit, findMainCredit]);

  const defaultSelectedFabric = useMemo(() => {
    return page.readySolutions?.colors?.find((color) => color.shopProductId === page.product.id);
  }, [page.readySolutions, page.product.id]);

  const countReviewsText = useMemo(() => {
    const reviews = (page.reviewsSubgallery || []).filter((review) => {
      return review.source === 'site';
    });

    const label = t('plural-reviews', reviews.length);

    return `${reviews.length} ${label}`;
  }, [page, t]);

  const handleClickShowroom = useCallback(() => {
    ModalWindows.open('Showrooms', { showrooms: showrooms.sellPoints });
  }, [showrooms?.sellPoints]);

  const handleClickFinalPrice = useCallback(() => {
    ModalWindows.open('FinalPrice');
  }, []);

  const handleClickWarranty = useCallback(async () => {
    ModalWindows.open('WarrantyProducts', {
      shopProductId: page.product.id,
      warranty: page.warranty,
    });
  }, [page.warranty, page.product.id]);

  const scrollToElem = useCallback(
    (elem: HTMLDivElement) => {
      const rect = elem.getBoundingClientRect();
      const top = window.scrollY + rect.top - totalHeight;

      window.scrollTo({ top, behavior: 'smooth' });
    },
    [totalHeight],
  );

  const handleBuyClick = useCallback(() => {
    if (!hasWarnings) return;

    if (isMobileM) {
      if (!refActions.current) return;

      scrollToElem(refActions.current);
    }

    setDisabled(true);

    setTimeout(() => {
      setDisabled(false);
    }, 1000);
  }, [isMobileM, hasWarnings, scrollToElem]);

  const handleClickDeliveryInformation = useCallback(() => {
    analytics.dispatchEvent('delivery.click', { url: pathname });

    ModalWindows.open('DeliveryInformation', {
      delivery: page.deliveryPage,
      goodsInfo,
      deliveryDate: showDeliveryDate && deliveryDate,
    });
  }, [page.deliveryPage, goodsInfo, deliveryDate, showDeliveryDate, analytics, pathname]);

  const handleClickNotifyAboutReceipt = useCallback(() => {
    ModalWindows.open('NotifyAboutReceipt', {
      product: page.product,
      group: page.modules.length !== 0,
    });
  }, [page.modules, page.product]);

  const handleClickReviews = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();

      if (onClickReviews) onClickReviews(e);
    },
    [onClickReviews],
  );

  const handleClickColor = useCallback(
    (e: MouseEvent, clickedFabric: ColorData) => {
      if (clickedFabric === defaultSelectedFabric) return;

      analytics.dispatchEvent('readySolution.select', {
        category: 'ColorCard',
        name: page.product.name,
      });
    },
    [defaultSelectedFabric, page.product.name, analytics],
  );

  const handleScroll = useCallback(() => {
    if (!refBuyButton.current) return;
    // кнопка Купить во вьюпорте
    setButtonInView(() => {
      return isInViewport({ elem: refBuyButton.current, offset: totalHeight });
    });
  }, [totalHeight]);

  const handleClickOption = useCallback((e: MouseEvent, params: Params) => {
    const { product, onApply } = params;
    ModalWindows.open('PickPairConstructor', { maxWidth: 645, product, onApply });
  }, []);

  useEffect(() => {
    handleScroll();
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  useUpdateComponentStorage({
    buyButton: page.isAvailable && (
      <StickyBuyButton visible={!buttonInView} onClick={handleBuyClick} />
    ),
  });

  return (
    <div {...restProps} className={className}>
      <div className={styles.shortName}>
        {page.product.model}
        {page.isAvailable && !page.isConfigurator && (
          <ProductLike
            className={styles.like}
            product={page.product}
            data-testid='favorites-card'
          />
        )}
      </div>
      <h1 className={styles.fullName} itemProp='name'>
        {getProductName(page.product)}
      </h1>

      {isComingSoon ? (
        <div className={styles.soonWrapper}>
          <Price
            className={cn(styles.actualPrice, {
              [styles.promo]: page.product.promo,
              [styles.longPrice]: isUzb,
            })}
            price={currentPrice.actual}
            itemProp='price'
            content={currentPrice.actual.toString()}
          />

          <OnSaleSoon className={styles.soonBlock}>
            <AboutReceiptButton product={page.product} className={styles.soonButton} />
          </OnSaleSoon>
        </div>
      ) : (
        <>
          <div className={styles.wrapperRating}>
            <div className={styles.iconMessageWrapper}>
              <IconEmptyMessage />
            </div>
            <Link
              className={styles.countReviews}
              view='primary'
              underlined
              onClick={handleClickReviews}
              data-testid='sidebar-feedback'
            >
              {countReviewsText}
            </Link>
          </div>

          {isBonusesShown && (
            <div className={styles.bonusesWrapper}>
              <AsyncOrderBonuses
                isSetConfigurator
                productIds={page.isConfigurator ? ids : [page.product.id]}
              />
              {isRus && !region.isFranchise && (
                <YandexSplitBadge amount={String(currentPrice.actual)} />
              )}
            </div>
          )}

          {page.isAvailable ? (
            <>
              <div className={styles.wrapperPrice}>
                <div
                  className={styles.containerPrices}
                  itemProp='offers'
                  itemScope
                  itemType='http://schema.org/Offer'
                >
                  <meta itemProp='priceCurrency' content={currency} />
                  <Price
                    className={cn(styles.actualPrice, {
                      [styles.promo]: page.product.promo,
                      [styles.longPrice]: isUzb,
                    })}
                    price={currentPrice.actual}
                    itemProp='price'
                    content={currentPrice.actual.toString()}
                  />
                  <link itemProp='availability' href='http://schema.org/InStock' />
                  &nbsp;
                  {hasExpired && (
                    <Price
                      expired
                      className={cn(styles.expiredPrice, { [styles.longPrice]: isUzb })}
                      price={currentPrice.expired}
                      withoutCurrency={isUzb}
                    />
                  )}
                  {hasDiscount && (
                    <Discount
                      className={cn(styles.discount, { [styles.longPrice]: isUzb })}
                      value={currentPrice.discount}
                      view='grayStroke'
                    />
                  )}
                </div>
              </div>

              {!!mainCredit && (
                <CreditTabWrapper
                  className={styles.creditTab}
                  key={mainCredit.id}
                  product={page.product}
                  pageCredit={mainCredit}
                />
              )}
            </>
          ) : (
            <div className={styles.wrapperOutOfStock}>
              <OutOfStock text={textOutOfStock} />
            </div>
          )}

          {page.readySolutions?.colors?.length > 0 && typeof page.readySolutions === 'object' && (
            <div className={styles.wrapperFabrics}>
              {hasReadySolutions && (
                <Fabrics
                  fabrics={page.readySolutions.colors}
                  count={page.readySolutions.configuratorCount}
                  defaultSelectedFabric={defaultSelectedFabric}
                  size='l'
                  onClickColor={handleClickColor}
                />
              )}
            </div>
          )}

          <div className={styles.actions} ref={refActions}>
            {page.isAvailable ? (
              <>
                {products.map((item, index) => {
                  return (
                    <Option
                      key={`products_${item.id}_${index}`}
                      product={item}
                      className={styles.option}
                      error={disabled}
                      onClickOption={(e) =>
                        handleClickOption(e, {
                          product: item,
                          onApply: PickPairStore.changeByType,
                        })
                      }
                      data-testid='option'
                    />
                  );
                })}

                {additional.products.map((item, index) => (
                  <Option
                    key={`additional_${item.id}_${index}`}
                    product={item}
                    className={styles.option}
                    canBeDeleted
                    error={disabled}
                    onClickOption={(e) =>
                      handleClickOption(e, {
                        product: item,
                        onApply: (product) => PickPairStore.changeAdditionalByIndex(product, index),
                      })
                    }
                    data-testid='additional-option'
                  />
                ))}

                {actions.map((item, index) => (
                  <div
                    key={`actions_${item.product.id}_${index}`}
                    className={cn(styles.addOption, { [styles.active]: item.active })}
                    onClick={(e) =>
                      handleClickOption(e, { product: item.product, onApply: PickPairStore.add })
                    }
                    data-testid='add-option'
                  >
                    {item.title}
                    <IconPlusBold className={styles.addOptionIcon} />
                  </div>
                ))}

                <div
                  className={cn(styles.action, { [styles.button]: true })}
                  ref={refBuyButton}
                  onClick={handleBuyClick}
                >
                  <BuyButton />
                </div>
              </>
            ) : (
              <Button
                className={cn(styles.action, { [styles.button]: true })}
                wide
                theme='secondary'
                onClick={handleClickNotifyAboutReceipt}
              >
                {t('ui.notify-about-receipt')}
              </Button>
            )}
          </div>

          <div className={styles.linksList}>
            {/*TODO: подставить данные с backend*/}
            <LinksList
              items={[
                (page.deliveryPage?.tables ||
                  page.deliveryPage?.deliveryTypes ||
                  page.deliveryPage?.delivery ||
                  page.deliveryPage?.teaser) && {
                  testid: 'link-to-delivery-info',
                  icon: (
                    <div
                      className={cn(styles.icon, {
                        [styles.delivery]: isRus,
                        [styles.deliveryOld]: !isRus,
                      })}
                    />
                  ),
                  hasArrow: true,
                  label: page.deliveryPage.title,
                  addingText: deliveryPrice && !region.isFranchise && (
                    <div>
                      {t('deliver')} {showDeliveryDate && deliveryDate} {t('for')}{' '}
                      <Price price={deliveryPrice} />
                    </div>
                  ),
                  onClick: handleClickDeliveryInformation,
                },
                currentPrice.isFinal && {
                  icon: <div className={cn(styles.icon, styles.attention)} />,
                  hasArrow: true,
                  label: t('links.price-final'),
                  onClick: handleClickFinalPrice,
                  testid: 'final-price',
                },
                page.warranty && {
                  icon: <div className={cn(styles.icon, styles.warranty)} />,
                  hasArrow: true,
                  label: page.warranty.sideBarTitle,
                  onClick: handleClickWarranty,
                },
                showrooms?.sellPoints?.length > 0 && {
                  icon: <div className={cn(styles.icon, styles.sellPoint)} />,
                  label: t('links.where-to-watch'),
                  hasArrow: true,
                  addingText: (
                    <div>
                      {t('model-is')} {showrooms.sellPoints.length}{' '}
                      {t('ui.plural-showrooms', showrooms.sellPoints.length)}
                    </div>
                  ),
                  onClick: handleClickShowroom,
                  testid: 'open-showrooms-modal',
                },
                {
                  label: t('characteristics'),
                  onClick: onClickCharacteristics,
                  testid: 'sidebar-characteristics',
                },
                {
                  label: t('links.reviews'),
                  onClick: onClickReviews,
                  testid: 'sidebar-feedback-and-photo',
                },
              ].filter(Boolean)}
            />
          </div>

          {isRus && mainCredit?.yandexSplitAvailable && mainCredit?.promoTag && (
            <YandexSplitWidget
              className={styles.yandexWidget}
              type='splitInfo'
              totalAmount={String(page.product.price.actual)}
            />
          )}
        </>
      )}
    </div>
  );
};

export default memo(Sidebar);
